import React from 'react';
import { ShoppingBag } from 'lucide-react';
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';

const About: React.FC = () => {
  const qualityFeatures = [
    { title: "High Quality Collar", image: "/aboutimg/collar.jpg" },
    { title: "Premium Fabrics", image: "/aboutimg/fabric1.png" },
    { title: "Mother of Pearl Buttons", image: "/aboutimg/pearl1.jpeg" },
    { title: "Clean Finished Buttonholes", image: "/aboutimg/finishedbutton.jpeg" },
    { title: "Pattern Matching", image: "/aboutimg/matching.png" },
    { title: "Clean, Tight Stitching", image: "/aboutimg/stitches.gif" },
    { title: "Single Needle Side Seam", image: "/aboutimg/signleneedle.png" },
    { title: "Tightly Sewn Buttons", image: "/aboutimg/finishedbutton.jpeg" },
    { title: "Spare Buttons", image: "/aboutimg/sparebutton.png" },
    { title: "Pinless Packing", image: "/aboutimg/packing.png" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section (moved up a bit) */}
      <section className="pt-12 pb-10 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="text-center mb-8">
          <motion.h1
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, y: -60, rotate: -5 }}   // 👈 slide down + tilt
            whileInView={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: false, amount: 0.3 }}   // 👈 replay every time
          >
              About Us
            </motion.h1>
          </div>

           {/* Paragraphs */}
            <motion.div
              className="max-w-6xl mx-auto space-y-4 text-gray-700 text-[14px] md:text-base text-justify leading-relaxed"
              initial={{ opacity: 0, x: -80 }}   // 👈 slide in from left
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
              viewport={{ once: false, amount: 0.2 }}   // 👈 replay every time
            >
            <p>
              Known for its perfection since 1993, <span className="text-yellow-600 font-semibold">GANI Shirts</span> meets the need of the modern professional with stylish classics. GANI is a brand in manufacturing high quality shirts without compromise. Our shirts are crafted from the <span className="text-yellow-600 font-medium">finest Italian, Egyptian and Turkey fabrics</span>.These fabrics are an extraordinary staple length and unique uniformity which makes them exceptional.
            </p>
            <p>
             GANI has a long and intensive clothing legacy, developing a keen sense of how fine tailoring works for men. We have a modern approach to design, by artfully using 100% linen cotton fabrics with made to measure styling in a line of ultra-lightweight shirts that makes men effortlessly stylish.
            </p>
            <p>
              We focus on creating the world’s finest linen and cotton shirts for our valuable customers all over the world.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quality Features Section */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <motion.h2
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              The Things We Take Care to Produce Our Shirts
            </motion.h2>
             {/* <div className="w-20 h-1 bg-yellow-500 mx-auto" />*/}
          </div>

          {/* Responsive Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
            {qualityFeatures.map((feature, i) => (
              <motion.div
                key={i}
                className="group rounded-2xl border border-gray-200 bg-white shadow-[0_2px_18px_rgba(0,0,0,0.05)] hover:shadow-md transition-all duration-200"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: false, amount: 0.2 }}
              >
                <div className="h-48 flex flex-col items-center justify-between px-3 pt-5 pb-4">
                  {/* Floating image */}
                  <motion.div
                    className="h-28 w-full flex items-center justify-center"
                    animate={{ y: [0, -10, 0] }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: i * 0.2,   // stagger start for variety
                    }}
                  >
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="w-20 h-20 object-contain"
                    />
                  </motion.div>

                  {/* Title */}
                  <div className="h-8 flex items-center justify-center text-center">
                    <h3 className="text-[14px] md:text-base text-gray-700 max-w-3xl mx-auto">
                      {feature.title}
                    </h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.p
            className="max-w-3xl mx-auto text-center text-gray-700 text-[14px] md:text-base  leading-relaxed mt-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            viewport={{ once: true }}
          >
            Each and every shirt goes in to <span className="text-yellow-600 font-semibold">five quality checks</span>  before it goes to our customers. We are happy when our customers feel the best when they wear GANI shirts.
          </motion.p>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 bg-gray-700 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: "url('/about_banner.jpg')" }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end">
             <motion.div
              className="max-w-md"
              initial={{ opacity: 0, x: 80 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="bg-black/50 backdrop-blur-sm rounded-lg p-6">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Best Quality and Affordable Price
                </h2>
                <p className="text-[14px] md:text-base  text-gray-200 mb-5">Enjoy your purchasing with Us</p>
                <Link
                  to="/collection"
                  className="group bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 inline-flex items-center gap-2 hover:scale-105 shadow-md hover:shadow-lg"
                >
                  <ShoppingBag className="w-5 h-5 transition-transform duration-300 group-hover:rotate-12" />
                  <span>Shop Now</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
